import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { ReactNode, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import PlusIcon from '@/assets/icons/plus-sm.svg';
import { Button } from '@/components/ui/button';
import { Counter } from '@/components/ui/counter';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { queryKeys } from '@/query-keys';

import { UtmMappingDialog } from '../utm-mapping-dialog';
import { NonIntegratedChannelsTable } from './table';
import { UnmappedUtmSources } from './unmapped-utms';

/* Layout component with all static content, to avoid code duplication in skeleton */
function NonIntegratedChannelsLayout({
  children,
  counter,
  button,
}: {
  children: ReactNode;
  counter: ReactNode;
  button: ReactNode;
}) {
  return (
    <div className="border-fx-neutral-200 flex flex-col rounded-md border bg-white">
      <div className="flex items-center justify-between p-4">
        {/* Title and description */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Typography variant="medium2" as="h2">
              <FormattedMessage
                defaultMessage="Non-integrated channels"
                description="Title for the non-integrated channels"
              />
            </Typography>
            {counter}
          </div>
          <Typography variant="body1">
            <FormattedMessage
              defaultMessage="Non-integrated channels allow you to manually map UTM sources for tracking."
              description="Description for the non-integrated channels"
            />
          </Typography>
        </div>

        {/* Add channel / map UTM sources button */}
        {button}
      </div>

      {children}

      <UnmappedUtmSources />
    </div>
  );
}

export function NonIntegratedChannels() {
  const [openDialog, setOpenDialog] = useState(false);
  const [manageChannelId, setManageChannelId] = useState<string>(); // Used to track the channel on which manage is clicked

  const {
    data: { sourcing_channels: channels },
  } = useSuspenseQuery({
    queryKey: [...queryKeys.settings.channels.nonIntegrated],
    queryFn: () =>
      SourcingService.getInternalApiSourcingSourcingChannelsNonIntegratedSourcingChannels(),
  });

  const mappedChannels = useMemo(
    () => channels.filter(channel => channel.utm_sources.length > 0),
    [channels],
  );

  const mappedChannelsCount = mappedChannels.length;

  const handleManageChannel = (channelId: string) => {
    setManageChannelId(channelId);
    setOpenDialog(true);
  };

  const handleDialogOpenChange = (open: boolean) => {
    setOpenDialog(open);
    if (!open) setManageChannelId(undefined);
  };

  return (
    <>
      <NonIntegratedChannelsLayout
        counter={
          mappedChannelsCount > 0 && <Counter count={mappedChannelsCount} />
        }
        button={
          <Button
            variant="secondary"
            type="button"
            onClick={() => setOpenDialog(true)}
            className="h-10"
          >
            <img src={PlusIcon} alt="Add" />
            <FormattedMessage
              defaultMessage="Add Channel / Map UTM Sources"
              description="Button to add a channel or map UTM sources"
            />
          </Button>
        }
      >
        <NonIntegratedChannelsTable
          mappedChannels={mappedChannels}
          onManageChannel={handleManageChannel}
        />
      </NonIntegratedChannelsLayout>

      <UtmMappingDialog
        open={openDialog}
        onOpenChange={handleDialogOpenChange}
        manageChannelId={manageChannelId}
      />
    </>
  );
}

NonIntegratedChannels.Skeleton = function NonIntegratedChannelsSkeleton() {
  return (
    <NonIntegratedChannelsLayout
      counter={<Skeleton className="h-4 w-6 rounded-full" />}
      button={<Skeleton className="w-68 h-10 rounded-md" />}
    >
      <NonIntegratedChannelsTable.Skeleton />
    </NonIntegratedChannelsLayout>
  );
};
